import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from 'src/environments/environment';
import { UserService } from './user.service';
import { Store, select } from '@ngrx/store';
import * as fromRoot from '../reducers';
import { Subscription } from 'rxjs';
import { linkURLSanitizer } from '../util/blend-helper';
import { Region } from '../shared/models';

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  private userId: number;
  private userSubscription: Subscription;
  constructor(
    private userService: UserService,
    private store: Store<fromRoot.State>,
  ) {
    // do not initialize in dev mode
    if (!environment.isDev) {
      this.initSentry();
    }
  }

  private initSentry(): void {
    Sentry.init({
      environment: environment?.production ? 'production' : 'staging',
      dsn: 'https://dba09366bb96170e15c6da769829adf2@o4507827516342272.ingest.de.sentry.io/4508003053994064',
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      tracesSampleRate: environment.tracesSampleRate,
      // Drop transactions for the high-traffic public download page (/dl/): its only
      // performance signal is a known render-blocking vendor bundle and it dominates quota.
      ignoreTransactions: ['/dl/'],
      // Session Replay — sample rates are environment-driven (see environment.*.ts).
      replaysSessionSampleRate: environment.replaysSessionSampleRate,
      replaysOnErrorSampleRate: environment.replaysOnErrorSampleRate,
      beforeSend: (event) => {
        const isDisableanalytics = this.userService.user && this.userService.get('is_disableanalytics'); // Get the user from the event

        // Not sending events if user has disabled analytics
        if (isDisableanalytics) {
          return null; // Do not send the event
        }

        // Scrub sensitive data from request URL
        if (event.request && event.request.url) {
          event.request.url = linkURLSanitizer(event.request.url);
        }

        // Scrub sensitive data from navigation breadcrumbs
        if (event.breadcrumbs) {
          event.breadcrumbs.forEach((breadcrumb, index) => {
            if (breadcrumb.category === 'navigation' && breadcrumb.data) {
              if (breadcrumb.data.from) {
                event.breadcrumbs[index].data.from = linkURLSanitizer(
                  'https://sentry' + breadcrumb.data.from,
                ).substring(14);
              }
              if (breadcrumb.data.to) {
                event.breadcrumbs[index].data.to = linkURLSanitizer('https://sentry' + breadcrumb.data.to).substring(
                  14,
                );
              }
            }
          });
        }

        return event;
      },
      beforeSendTransaction: (event) => {
        const isDisableanalytics = this.userService.user && this.userService.get('is_disableanalytics'); // Get the user from the event

        // Not sending events if user has disabled analytics
        if (isDisableanalytics) {
          return null; // Do not send the event
        }
        return event;
      },
    });

    // setting default userId and region in case user is not logged in
    const region =
      window.location.hostname === 'us.sync.com' || window.location.hostname === 'ln6.sync.com'
        ? Region.USCENTRAL
        : Region.CACENTRAL;
    Sentry.setUser({
      id: 'unknown',
      region,
    });
    Sentry.setTag('region', region);

    // setting userId and region when user is logged in.
    this.userSubscription = this.store.pipe(select(fromRoot.getAuthUser)).subscribe((data) => {
      if (data && data.id) {
        if (this.userId !== data.id) {
          Sentry.setUser({
            id: data.id,
            region: data.region,
          });
          Sentry.setTag('region', data.region);
          this.userId = data.id;
        }
      }
    });
  }

  public logError(error: any, additionalContext: any): void {
    if (!environment.isDev) {
      Sentry.captureException(error, (scope: Sentry.Scope) => {
        scope.setExtra('context', additionalContext);
        return scope;
      });
    }
  }
}
